import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  History, 
  RefreshCw, 
  ScanBarcode, 
  Settings, 
  ChevronRight,
  Clock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { useLanguage } from '../../../contexts/LanguageContext';

const HISTORY_LIMIT_KEY = 'dealshaq_history_limit';
const DEFAULT_LIMIT = 5;
const MAX_LIMIT = 25;

export default function QuickScanHistory({ onRepost }) {
  const [recentScans, setRecentScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [historyLimit, setHistoryLimit] = useState(DEFAULT_LIMIT);
  const navigate = useNavigate();
  const { t } = useLanguage();

  useEffect(() => {
    // Load saved preference
    const savedLimit = localStorage.getItem(HISTORY_LIMIT_KEY);
    if (savedLimit) {
      const parsed = parseInt(savedLimit, 10);
      if (parsed >= 1 && parsed <= MAX_LIMIT) {
        setHistoryLimit(parsed);
      }
    }
  }, []);

  useEffect(() => {
    fetchRecentScans();
  }, [historyLimit]);

  const fetchRecentScans = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/barcode/history?limit=${historyLimit}`);
      if (response.data.success) {
        setRecentScans(response.data.scans);
      }
    } catch (error) {
      console.error('Failed to fetch recent scans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRepost = (scan) => {
    // Navigate to scanner with pre-filled data
    navigate('/retailer/scan', { 
      state: { 
        repostData: {
          raw_code: scan.raw_code,
          format: scan.format,
          product_name: scan.product_name,
          category: scan.category
        }
      } 
    });
  };

  const handleLimitChange = (value) => {
    const newLimit = parseInt(value, 10);
    setHistoryLimit(newLimit);
    localStorage.setItem(HISTORY_LIMIT_KEY, String(newLimit));
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('retailer.dashboard.just_now', 'Just now');
    if (diffMins < 60) return `${diffMins}m ${t('retailer.dashboard.ago', 'ago')}`;
    if (diffHours < 24) return `${diffHours}h ${t('retailer.dashboard.ago', 'ago')}`;
    if (diffDays < 7) return `${diffDays}d ${t('retailer.dashboard.ago', 'ago')}`;
    return date.toLocaleDateString();
  };

  const limitOptions = [5, 10, 15, 20, 25];

  return (
    <Card className="h-fit">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <History className="w-4 h-4 text-blue-600" />
            {t('retailer.dashboard.quick_scan_history', 'Quick Scan History')}
          </CardTitle>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setShowSettings(!showSettings)}
              data-testid="history-settings-btn"
            >
              <Settings className="w-4 h-4 text-gray-500" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={fetchRecentScans}
              disabled={loading}
              data-testid="history-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
        <CardDescription className="text-xs">
          {t('retailer.dashboard.quick_scan_desc', 'One-click to re-post popular items')}
        </CardDescription>
      </CardHeader>
      
      <CardContent className="pt-0">
        {/* Settings Panel */}
        {showSettings && (
          <div className="mb-3 p-3 bg-gray-50 rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {t('retailer.dashboard.show_last', 'Show last')}
              </span>
              <Select value={String(historyLimit)} onValueChange={handleLimitChange}>
                <SelectTrigger className="w-20 h-8" data-testid="history-limit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {limitOptions.map(opt => (
                    <SelectItem key={opt} value={String(opt)}>{opt}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Scan List */}
        {loading ? (
          <div className="py-4 text-center text-gray-500 text-sm">
            {t('common.loading', 'Loading...')}
          </div>
        ) : recentScans.length === 0 ? (
          <div className="py-6 text-center">
            <ScanBarcode className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">
              {t('retailer.scanner.no_recent_scans', 'No recent scans')}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => navigate('/retailer/scan')}
            >
              {t('retailer.dashboard.start_scanning', 'Start Scanning')}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {recentScans.map((scan, index) => (
              <div 
                key={`${scan.raw_code}-${index}`}
                className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-colors group"
                data-testid={`history-item-${index}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono bg-gray-100 px-1.5 py-0.5 rounded">
                      {scan.raw_code}
                    </code>
                    <Badge variant="outline" className="text-[10px]">
                      {scan.format}
                    </Badge>
                  </div>
                  {scan.product_name && (
                    <p className="text-sm text-gray-700 truncate mt-0.5">
                      {scan.product_name}
                    </p>
                  )}
                  <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                    <Clock className="w-3 h-3" />
                    {formatTimestamp(scan.scanned_at)}
                    {scan.created_rshd && (
                      <Badge variant="secondary" className="text-[9px] ml-1 bg-green-100 text-green-700">
                        {t('retailer.dashboard.posted', 'Posted')}
                      </Badge>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-2 opacity-70 hover:opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                  onClick={() => handleRepost(scan)}
                  data-testid={`repost-btn-${index}`}
                >
                  <ChevronRight className="w-4 h-4" />
                  <span className="text-xs">{t('retailer.dashboard.repost', 'Re-post')}</span>
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* View All Link */}
        {recentScans.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-2 text-xs text-gray-500"
            onClick={() => navigate('/retailer/scan')}
          >
            {t('retailer.dashboard.view_all_scanner', 'View all in Scanner')}
            <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
